import { Brain, ExternalLink, Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { checkMemoriesExist } from "@/services/memoryService";
import useAuthStore from "@/store/authStore";

/**
 * Format extraction time for display
 * Shows relative time (e.g., "2 hours ago") for recent extractions,
 * or date for older ones
 */
function formatExtractionTime(isoString) {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    // For older dates, show the actual date
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  } catch {
    return null;
  }
}

const CATEGORY_COLORS = {
  preference:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  project:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  profile:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  constraint:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  goal: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  general: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

/**
 * Get a summary of categories for the button text
 * @param {Array} memories - Array of memory objects
 * @returns {string} Text like "1 preference remembered" or "3 remembered"
 */
function getCategorySummary(memories) {
  if (!memories?.length) return "";

  const categories = memories.map((m) => m.category).filter(Boolean);
  const uniqueCategories = [...new Set(categories)];

  if (uniqueCategories.length === 1) {
    const category = uniqueCategories[0];
    const count = memories.length;
    // Pluralize category name
    const categoryLabel = count === 1 ? category : `${category}s`;
    return `${count} ${categoryLabel} remembered`;
  }

  return `${memories.length} remembered`;
}

/**
 * MemoryIndicator - Displays an inline indicator when memories are extracted from a message
 * Shows a brain icon with count, and a popover with memory details on click
 *
 * @component
 * @example
 * <MemoryIndicator memories={[{ id: '1', content: 'User prefers TypeScript', category: 'preference' }]} />
 */
// Memory system disabled for production release
const MEMORY_SYSTEM_ENABLED = false;

export function MemoryIndicator({ memories, extractionTime, workspaceId, className }) {
  // All hooks must be called before any conditional returns (React rules of hooks)
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [memoryStatuses, setMemoryStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);

  // Lazy load memory statuses when popover opens
  const checkMemoryStatuses = useCallback(async () => {
    if (statusChecked || !user?.id || !memories?.length) return;

    setStatusLoading(true);
    try {
      const memoryIds = memories.map((m) => m.id).filter(Boolean);
      if (memoryIds.length > 0) {
        const statuses = await checkMemoriesExist(memoryIds, user.id);
        setMemoryStatuses(statuses);
      }
    } catch (error) {
      console.error("Failed to check memory statuses:", error);
    } finally {
      setStatusLoading(false);
      setStatusChecked(true);
    }
  }, [memories, user?.id, statusChecked]);

  // Handle popover open/close
  const handleOpenChange = (open) => {
    if (open) {
      checkMemoryStatuses();
    }
  };

  // Navigate to memory settings
  const handleViewAllMemories = () => {
    if (workspaceId) {
      // For project threads, navigate to project page with dialog param
      navigate(`/projects/${workspaceId}?dialog=memories`);
    } else {
      // For general threads, navigate to settings page with memories dialog
      navigate("/settings?tab=personalization&dialog=memories");
    }
  };

  // Memory system disabled for production - return null after all hooks
  if (!MEMORY_SYSTEM_ENABLED) {
    return null;
  }

  if (!memories?.length) return null;

  const formattedTime = formatExtractionTime(extractionTime);
  const buttonText = getCategorySummary(memories);

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1 px-2 py-1 rounded-full w-fit",
            "text-xs text-purple-600 dark:text-purple-400",
            "bg-purple-50 dark:bg-purple-900/20",
            "hover:bg-purple-100 dark:hover:bg-purple-900/30",
            "transition-colors cursor-pointer",
            "border border-purple-200 dark:border-purple-800/50",
            className
          )}
          aria-label={`${memories.length} ${memories.length === 1 ? "memory" : "memories"} extracted`}
        >
          <Brain className="h-3 w-3" />
          <span>{buttonText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
              <Brain className="h-4 w-4 text-purple-500" />
              Memories extracted
            </div>
            {formattedTime && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {formattedTime}
              </span>
            )}
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {memories.map((memory, index) => {
              // Get memory text content - try multiple field names
              const memoryText =
                (memory.content && memory.content.trim()) ||
                (memory.memory && memory.memory.trim()) ||
                (memory.text && memory.text.trim()) ||
                "(Memory content unavailable)";

              // Get status - only show after loading
              const status = memoryStatuses[memory.id];
              const showStatus = statusChecked && memory.id;

              return (
                <div
                  key={memory.id || index}
                  className="p-2 rounded-md bg-gray-50 dark:bg-zinc-800 text-sm"
                >
                  <p className="text-gray-700 dark:text-gray-300 line-clamp-3">
                    {memoryText}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {memory.category && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] px-1.5 py-0 border-0",
                          CATEGORY_COLORS[memory.category] || CATEGORY_COLORS.general
                        )}
                      >
                        {memory.category}
                      </Badge>
                    )}
                    {memory.importance === "high" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 border-0 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"
                      >
                        important
                      </Badge>
                    )}
                    {/* Status badge - lazy loaded */}
                    {statusLoading && !statusChecked && (
                      <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                    )}
                    {showStatus && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] px-1.5 py-0 border-0",
                          status
                            ? "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                        )}
                      >
                        {status ? "Active" : "Deleted"}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* View all memories link */}
          <button
            onClick={handleViewAllMemories}
            className="flex items-center gap-1.5 w-full pt-2 border-t border-gray-200 dark:border-zinc-700 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            <span>{workspaceId ? "View project memories" : "View all memories"}</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

MemoryIndicator.propTypes = {
  /** Array of extracted memories to display */
  memories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      content: PropTypes.string,
      memory: PropTypes.string, // Alternative field name from API
      category: PropTypes.string,
      importance: PropTypes.string,
    })
  ),
  /** ISO timestamp of when memories were extracted (for historical reference) */
  extractionTime: PropTypes.string,
  /** Workspace ID for project-scoped memories (determines navigation target) */
  workspaceId: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default MemoryIndicator;
